import useApp from 'hooks/useApp';
import useData from 'hooks/useData';
import { useTranslation } from 'react-i18next';
import type { EditableModulationCellProps } from 'types/appContext';
import type { DummyCoworker } from 'types/scenario';
import { getGreyedOutCell } from 'components/GreyedOutCell/helpers';
import { useMemo } from 'react';
import { RangeStatus } from 'types/enums';
import { type Coworker, EmploymentStatus } from 'types/coworker';
import { costCentreIsInList } from 'utils/text';
import classes from './ModulationSubRow.module.scss';
import ModulationCell from './ModulationCell';

export const ModulationSubRow = ({
    coworker,
    cellProps,
    canEdit,
    isActive,
    contractStartDate,
    contractEndDate,
    showAllDetails,
    costCentre,
}: {
    coworker: Coworker | DummyCoworker,
    cellProps: Array<EditableModulationCellProps>,
    canEdit: boolean,
    isActive: boolean,
    contractStartDate:string,
    contractEndDate:string,
    showAllDetails: boolean,
    costCentre: string,
}) => {
    const { t } = useTranslation();
    const { currentScenario } = useData();
    const { timeSelection, orgSelection: { utils: { selectedCostCentres } } } = useApp();

    const cells = useMemo(
        () => cellProps.map((cProps, index) => {
            const { elementId, key, currentHours, currentMinutes } = cProps;
            const contractAbsentCell = getGreyedOutCell(
                contractStartDate,
                contractEndDate,
                timeSelection.timeArray,
                index,
                key,
            );

            if (contractAbsentCell !== RangeStatus.OUTSIDE_RANGE) {
                return contractAbsentCell;
            }

            return (
                <ModulationCell
                    elementId={elementId}
                    currentHours={currentHours}
                    currentMinutes={currentMinutes}
                    canBeEdited={canEdit}
                    key={key}
                    isActive={isActive && costCentreIsInList(costCentre, selectedCostCentres.map(cc => cc.costCentreCode) ?? [])}
                />
            );
        }).filter(el => el.toString() !== RangeStatus.OUTSIDE_RANGE && el.toString() !== RangeStatus.WITHIN_RANGE),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cellProps, currentScenario, coworker]
    );

    return (
        <tr className={(coworker.status === EmploymentStatus.ACTIVE || currentScenario) ? '' : classes.inactive}>
            <td className={classes['modulation-row__title-cell']}>
                <b className={classes['modulation-row__title-cell--title']}>{t('MODULATION_HOURS')}</b>
            </td>
            {showAllDetails && (
                <>
                    <td aria-labelledby="modulation-row__empty-cell" className={classes['modulation-row__empty-cell']} />
                    <td aria-labelledby="modulation-row__empty-cell" className={classes['modulation-row__empty-cell']} />
                    <td aria-labelledby="modulation-row__empty-cell" className={classes['modulation-row__empty-cell']} />
                    <td aria-labelledby="modulation-row__empty-cell" />
                </>
            )}
            {cells}
            <td aria-labelledby="modulation-row__empty-cell" />
        </tr>
    );
};
